
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { StatsResult } from '@/hooks/use-studio-stats';
import { 
  ResponsiveContainer, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend,
  PieChart,
  Pie,
  Cell 
} from 'recharts';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';

interface ServiceInsightsProps {
  stats: StatsResult;
  isLoading: boolean;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

const ServiceInsights: React.FC<ServiceInsightsProps> = ({ stats, isLoading }) => {
  // Only show the top 5 services for the pie chart
  const topServices = [...stats.servicePerformance]
    .sort((a, b) => b.bookings - a.bookings)
    .slice(0, 5);
  
  // Calculate percentages for the pie chart
  const totalBookings = topServices.reduce((total, service) => total + service.bookings, 0);
  const servicesWithPercentage = topServices.map(service => ({
    ...service,
    percentage: Math.round((service.bookings / totalBookings) * 100)
  }));
  
  // Find services with 0 bookings
  const unusedServices = stats.servicePerformance.filter(service => service.bookings === 0);
  
  return (
    <div className="space-y-6">
      {/* Top Services */}
      <Card>
        <CardHeader>
          <CardTitle>Beliebteste Leistungen</CardTitle>
          <CardDescription>
            Die am häufigsten gebuchten Leistungen im ausgewählten Zeitraum
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center items-center h-60">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-chrona-accent"></div>
            </div>
          ) : stats.servicePerformance.length === 0 ? (
            <div className="flex justify-center items-center h-60">
              <p className="text-muted-foreground">Keine Daten verfügbar</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Bar Chart */}
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    layout="vertical"
                    data={stats.servicePerformance.slice(0, 8)}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis type="category" dataKey="name" width={100} />
                    <Tooltip
                      formatter={(value) => [`${value} Buchungen`, 'Anzahl']}
                    />
                    <Bar dataKey="bookings" fill="#2C6E49" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              
              {/* Pie Chart */}
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={servicesWithPercentage}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="bookings"
                      nameKey="name"
                      label={({name, percentage}) => `${name}: ${percentage}%`}
                    >
                      {servicesWithPercentage.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [`${value} Buchungen`, 'Anzahl']} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Service Performance Table */}
      <Card>
        <CardHeader>
          <CardTitle>Leistungsstatistik</CardTitle>
          <CardDescription>
            Detaillierte Übersicht aller Leistungen
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center items-center h-40">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-chrona-accent"></div>
            </div>
          ) : stats.servicePerformance.length === 0 ? (
            <div className="flex justify-center items-center h-40">
              <p className="text-muted-foreground">Keine Daten verfügbar</p>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Leistungsname</TableHead>
                    <TableHead className="text-right">Anzahl Buchungen</TableHead>
                    <TableHead className="text-right">Anteil (%)</TableHead>
                    <TableHead className="text-right">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stats.servicePerformance.map((service) => (
                    <TableRow key={service.name}>
                      <TableCell className="font-medium">{service.name}</TableCell>
                      <TableCell className="text-right">{service.bookings}</TableCell>
                      <TableCell className="text-right">
                        {Math.round((service.bookings / totalBookings) * 100)}%
                      </TableCell>
                      <TableCell className="text-right">
                        {service.bookings > totalBookings * 0.2 ? (
                          <span className="text-green-600">Beliebt</span>
                        ) : service.bookings === 0 ? (
                          <span className="text-red-600">Ungenutzt</span>
                        ) : (
                          <span className="text-amber-600">Normal</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Unused Services */}
      {unusedServices.length > 0 && (
        <Card className="border-amber-500/30 bg-amber-500/10">
          <CardHeader>
            <CardTitle>Ungenutzte Leistungen</CardTitle>
            <CardDescription>
              Diese Leistungen wurden im ausgewählten Zeitraum nicht gebucht
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="list-disc pl-5 space-y-1">
              {unusedServices.map((service) => (
                <li key={service.name} className="text-muted-foreground">
                  {service.name}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ServiceInsights;
